import React, { useMemo } from 'react';
import { Block } from '../types/Block';
import { BlockCalculationService } from '../services/BlockCalculationService';
import {
  LotBudgetInfo,
  formatDeltaPct,
  formatDeltaAmount,
  severityTextClass,
} from '../utils/lotBudget';

interface RecapSummaryProps {
  blocks: Block[];
  lots: Block[];
  onSelectLot: (id: string) => void;
  lotBudgets?: Map<string, LotBudgetInfo>;
}

// Vue Récap : chaque lot = une ligne avec son total (somme du sous-arbre).
// Clic sur une ligne → ouvre l'onglet du lot.
const RecapSummary: React.FC<RecapSummaryProps> = ({ blocks, lots, onSelectLot, lotBudgets }) => {
  const rows = useMemo(() => {
    const childrenByParent = new Map<string, string[]>();
    for (const b of blocks) {
      if (b.parentId) {
        const arr = childrenByParent.get(b.parentId) ?? [];
        arr.push(b.id);
        childrenByParent.set(b.parentId, arr);
      }
    }
    const byId = new Map(blocks.map(b => [b.id, b]));
    return lots.map(lot => {
      // BFS descendants (hors lot lui-même)
      const descendants: Block[] = [];
      const frontier: string[] = [...(childrenByParent.get(lot.id) ?? [])];
      while (frontier.length) {
        const id = frontier.shift()!;
        const b = byId.get(id);
        if (!b) continue;
        descendants.push(b);
        const kids = childrenByParent.get(id);
        if (kids) frontier.push(...kids);
      }
      const total = BlockCalculationService.calculateTotal(descendants);
      const prestationCount = descendants.filter(b => !!b.content.intitule || !!b.content.prixUnitaire).length;
      return { lot, total, prestationCount };
    });
  }, [blocks, lots]);

  const grandTotal = rows.reduce((s, r) => s + r.total, 0);
  const grandInitial = rows.reduce((s, r) => {
    const b = lotBudgets?.get(r.lot.id);
    return s + (b?.hasInitial ? (b.initial as number) : 0);
  }, 0);
  const grandDelta = grandTotal - grandInitial;
  const grandDeltaPct = grandInitial > 0 ? (grandDelta / grandInitial) * 100 : 0;
  const hasAnyInitial = grandInitial > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Récap du chiffrage</h3>
        <p className="text-xs text-gray-500 mt-0.5">Somme par lot — clic pour ouvrir le lot</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-4">Lot</th>
              <th className="py-2 pr-4 text-right">Prestations</th>
              <th className="py-2 pr-4 text-right">Budget initial</th>
              <th className="py-2 pr-4 text-right">Total actuel</th>
              <th className="py-2 text-right">Delta</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-gray-400 text-sm italic">
                  Aucun lot — clique sur le « + » en haut à gauche pour en créer un.
                </td>
              </tr>
            ) : (
              rows.map(({ lot, total, prestationCount }) => {
                const budget = lotBudgets?.get(lot.id);
                return (
                  <tr
                    key={lot.id}
                    className="border-b border-gray-100 hover:bg-violet-50 cursor-pointer transition-colors"
                    onClick={() => onSelectLot(lot.id)}
                  >
                    <td className="py-3 pr-4 font-medium text-gray-800">{lot.content.titre || 'Sans titre'}</td>
                    <td className="py-3 pr-4 text-right text-gray-500">{prestationCount}</td>
                    <td className="py-3 pr-4 text-right text-gray-500 tabular-nums">
                      {budget?.hasInitial ? `${BlockCalculationService.formatPrice(budget.initial as number)} €` : '—'}
                    </td>
                    <td className="py-3 pr-4 text-right font-semibold text-gray-900 tabular-nums">
                      {BlockCalculationService.formatPrice(total)} €
                    </td>
                    <td className={`py-3 text-right tabular-nums font-medium ${budget?.hasInitial ? severityTextClass(budget.severity) : 'text-gray-300'}`}>
                      {budget?.hasInitial
                        ? `${formatDeltaPct(budget.deltaPct)} (${formatDeltaAmount(budget.delta)})`
                        : '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-gray-300">
                <td className="pt-3 pr-4 font-bold text-gray-900">Total chiffrage</td>
                <td className="pt-3" />
                <td className="pt-3 pr-4 text-right font-semibold text-gray-500 tabular-nums">
                  {hasAnyInitial ? `${BlockCalculationService.formatPrice(grandInitial)} €` : '—'}
                </td>
                <td className="pt-3 pr-4 text-right font-bold text-violet-700 text-base tabular-nums">
                  {BlockCalculationService.formatPrice(grandTotal)} €
                </td>
                <td className={`pt-3 text-right tabular-nums font-bold ${hasAnyInitial ? (Math.abs(grandDeltaPct) < 5 ? 'text-gray-500' : Math.abs(grandDeltaPct) < 15 ? 'text-orange-500' : 'text-orange-700') : 'text-gray-300'}`}>
                  {hasAnyInitial
                    ? `${formatDeltaPct(grandDeltaPct)} (${formatDeltaAmount(grandDelta)})`
                    : '—'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default RecapSummary;
